import React, { useContext, useState } from "react";
import { CircularProgress, Grid, makeStyles } from "@material-ui/core";
import { ClassroomApi, ClassroomDto } from "../../api-clients/api";
import { Dropdown, IDropdownOption, TextField } from "@fluentui/react";
import { getApiUrl } from "../../api-clients";
import { AppContext } from "../../contexts/AppContext";
import { SchoolContext } from "../../contexts/SchoolContext";
import FormButtons from "../Shared/FormButtons";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: theme.spacing(1),
    position: "relative",
  },
}));

interface IProps {
  classroom: ClassroomDto;
  classroomChanged?: () => void;
}
export default function CRUDClassroom({
  classroom,
  classroomChanged,
}: IProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { openSnackbar } = useContext(AppContext);
  const { educations, setClassroomUpdated } = useContext(SchoolContext);
  const [saving, setSaving] = useState<boolean>(false);
  const [classroomApi] = useState<ClassroomApi>(new ClassroomApi(getApiUrl()));
  const [newClassroom, setNewClassroom] = useState<ClassroomDto>(
    classroom ?? ({} as ClassroomDto)
  );

  const canSave: boolean =
    (newClassroom.name?.length ?? 0) > 0 &&
    (newClassroom.acronym?.length ?? 0) > 0 &&
    (newClassroom.educationId?.length ?? 0) > 0;

  const educationOptions: IDropdownOption[] = educations.map((e) => ({
    key: e.educationId ?? "",
    text: e.name ?? "",
  }));

  const saveClassroom = () => {
    setSaving(true);
    const action = newClassroom.classroomId
      ? classroomApi.update(newClassroom)
      : classroomApi.create(newClassroom);
    action
      .then(() => {
        setSaving(false);
        openSnackbar(
          newClassroom.classroomId
            ? t("classrooms.updateSuccess")
            : t("classrooms.saveSuccess"),
          "success"
        );
        if (!newClassroom.classroomId)
          setNewClassroom({ name: "", acronym: "", enum: 0 } as ClassroomDto);
        setClassroomUpdated(true);
        if (classroomChanged) classroomChanged();
      })
      .catch(() => {
        setSaving(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  return (
    <div>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            required
            autoComplete="off"
            label={t("classrooms.name")}
            placeholder={t("classrooms.namePlaceholder")}
            value={newClassroom.name}
            onChange={(
              event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
              newValue?: string
            ) =>
              setNewClassroom({
                ...newClassroom,
                name: newValue,
              } as ClassroomDto)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            autoComplete="off"
            label={t("classrooms.acronym")}
            placeholder={t("classrooms.acronymPlaceholder")}
            value={newClassroom.acronym}
            onChange={(
              event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
              newValue?: string
            ) =>
              setNewClassroom({
                ...newClassroom,
                acronym: newValue,
              } as ClassroomDto)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Dropdown
            required
            label={t("classrooms.education")}
            placeholder={t("classrooms.selectEducation")}
            options={educationOptions}
            selectedKey={newClassroom.educationId}
            onChange={(
              event: React.FormEvent<HTMLDivElement>,
              option?: IDropdownOption
            ) => {
              if (option)
                setNewClassroom({
                  ...newClassroom,
                  educationId: option.key as string,
                } as ClassroomDto);
            }}
          />
        </Grid>
      </Grid>
      <div className={classes.wrapper}>
        <FormButtons
          onConfirmClick={saveClassroom}
          disabled={saving || !canSave}
          cancelButtonVisible={false}
          confirmIcon={newClassroom.classroomId != null ? "Save" : "Add"}
          confirmText={
            newClassroom.classroomId != null
              ? t("common.save")
              : t("common.create")
          }
        />
        {saving && (
          <CircularProgress
            size={24}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: -12,
              marginLeft: -12,
            }}
          />
        )}
      </div>
    </div>
  );
}
